// ============================================
// FILE: src/components/fashion/FashionProductCard.jsx
// ============================================
//
// The typographic hierarchy here is the point: brand bold and dark, product
// name quieter and truncated to one line, then the price row. Shoppers scan
// the brand first on a fashion grid, which is the opposite of grocery.
//
// There is no add-to-bag button. A size has to be chosen first, so the card
// opens the product page instead — which also sidesteps the variant-blind
// cart lookup the grocery cards do.

import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { resolveProductImageUrl, BLANK_PRODUCT_IMAGE, handleProductImageError } from '../../utils/imageHelpers';
import {
  getFinalPrice,
  getRegularPrice,
  getSalePrice,
  hasDiscount,
  getDiscountPercent,
} from '../../utils/priceHelpers';

const FashionProductCard = ({
  product,
  onOpen = () => {},
  toggleFavorite,
  favorites = [],
  isDesktop = false,
  index = 0,
  // Rails use a square, shorter card so more of the shelf is visible at once.
  // The category grid keeps the 3:4 portrait, where the taller crop is worth
  // the space.
  compact = false,
}) => {
  const [hovered, setHovered] = useState(false);

  const favArray = Array.isArray(favorites) ? favorites : [];
  const isFav = favArray.some((item) => String(item?.id) === String(product.id));

  const cover = resolveProductImageUrl(
    product.featured_image_url || product.image_url || product.thumbnail_url || product.featured_image
  ) || BLANK_PRODUCT_IMAGE;
  const hover = resolveProductImageUrl(product.hover_image_url);

  const discounted = hasDiscount(product);
  const range = product.price_range;
  const showFrom = product.has_variants && range && range.min !== range.max;

  const sizes = Array.isArray(product.sizes) ? product.sizes : [];
  const swatchCount = Array.isArray(product.swatches) ? product.swatches.length : 0;

  return (
    <div
      className="fx-card relative bg-white cursor-pointer"
      style={{ animationDelay: `${(index % 10) * 40}ms` }}
      onClick={() => onOpen(product)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="relative w-full bg-[#f5f5f6] overflow-hidden"
        style={{ aspectRatio: compact ? '1 / 1' : '3 / 4' }}
      >
        <img
          src={cover}
          alt={product.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
          style={{ opacity: isDesktop && hovered && hover ? 0 : 1 }}
          onError={handleProductImageError}
        />

        {isDesktop && hover && (
          <img
            src={hover}
            alt=""
            loading="lazy"
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
            style={{ opacity: hovered ? 1 : 0 }}
          />
        )}

        {/* Rating chip, bottom-left over the image */}
        {Number(product.rating) > 0 && (
          <div
            className="absolute bottom-0 left-0 flex items-center gap-1 bg-white/95 px-2 py-1"
            style={{ fontSize: 12, fontWeight: 700, color: 'var(--fx-ink)' }}
          >
            <span>{Number(product.rating).toFixed(1)}</span>
            <span style={{ color: 'var(--fx-green)', fontSize: 11 }}>★</span>
            {product.review_count > 0 && (
              <>
                <span style={{ color: 'var(--fx-line)' }}>|</span>
                <span style={{ color: 'var(--fx-ink-3)', fontWeight: 400 }}>
                  {product.review_count > 999
                    ? `${(product.review_count / 1000).toFixed(1)}k`
                    : product.review_count}
                </span>
              </>
            )}
          </div>
        )}

        {/* Desktop hover strip: available sizes + wishlist, Myntra-style.
            Skipped on compact cards — on a small square it would cover most of
            the photo, which is the one thing a rail card has to show. */}
        {isDesktop && !compact && (
          <div className="fx-hover absolute inset-x-0 bottom-0">
            {sizes.length > 0 && (
              <div
                className="bg-white/95 px-2 py-1.5 text-center"
                style={{ fontSize: 12, color: 'var(--fx-ink-2)' }}
              >
                Sizes: {sizes.slice(0, 6).map((s) => String(s).toUpperCase()).join(', ')}
              </div>
            )}
            {typeof toggleFavorite === 'function' && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggleFavorite(product); }}
                className="w-full flex items-center justify-center gap-2 bg-white py-2 border-t"
                style={{ borderColor: 'var(--fx-line)', fontSize: 13, fontWeight: 700, letterSpacing: '0.02em' }}
              >
                <Heart
                  size={15}
                  className={isFav ? 'text-[#ff3f6c]' : ''}
                  fill={isFav ? 'currentColor' : 'none'}
                />
                {isFav ? 'WISHLISTED' : 'WISHLIST'}
              </button>
            )}
          </div>
        )}

        {/* Mobile keeps a persistent heart, since there is no hover */}
        {!isDesktop && typeof toggleFavorite === 'function' && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggleFavorite(product); }}
            className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-full"
            aria-label={isFav ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <Heart
              size={15}
              className={isFav ? 'text-[#ff3f6c]' : 'text-[#94969f]'}
              fill={isFav ? 'currentColor' : 'none'}
            />
          </button>
        )}
      </div>

      <div className="px-2.5 pt-2 pb-3">
        <p className="fx-brand truncate">{product.brand || product.name}</p>
        {product.brand && <p className="fx-name mt-0.5">{product.name}</p>}

        <div className="flex items-baseline gap-1.5 mt-1.5 flex-wrap">
          {showFrom ? (
            <span className="fx-price">Rs. {range.min} onwards</span>
          ) : discounted ? (
            <>
              <span className="fx-price">Rs. {getSalePrice(product)}</span>
              <span className="fx-mrp">Rs. {getRegularPrice(product)}</span>
              <span className="fx-off">({getDiscountPercent(product)}% OFF)</span>
            </>
          ) : (
            <span className="fx-price">Rs. {getFinalPrice(product)}</span>
          )}
        </div>

        {/* Colourway count, the way a marketplace hints at variety */}
        {swatchCount > 1 && (
          <p className="mt-1" style={{ fontSize: 12, color: 'var(--fx-ink-3)' }}>
            +{swatchCount - 1} more colour{swatchCount > 2 ? 's' : ''}
          </p>
        )}
      </div>
    </div>
  );
};

export default FashionProductCard;
